import { Box, FlexLayout, H2 } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import { ComponentFactory } from '@deity-io/di-data-flows-editor';
import type { IconType } from '@deity-io/portal-ui';
import { TRow, TableView } from 'components';
import ActionPopover from 'components/PackageVersion/ActionPopover';
import { toLocalDateString } from 'contracts/constants';
import type { PopoverActionMenuOption } from 'contracts/interfaces/PopoverActionMenuOption';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useExecuteButtonCommandMutation } from 'services/graphql/generated/hooks';
import { v4 } from 'uuid';
import type { IRuntimeState } from '../IRuntimeState';
import { filterServiceType } from '../IRuntimeState';

type PackagePopoutProducerProps = {
  producer: {
    type: string;
    since: number;
    count: number;
    instanceCount: number;
    initialized: number;
    instances: Record<string, IPropertyBag>;
  };
  producerKey: string;
  flowKey?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  pipelineKey: string;
  accountUuid: string;
  packageVersionUuid: string;
  services: IRuntimeState['services'];
};

type RunButtonProps = {
  hosts: string[];
  onClick: (selected: string | undefined) => void;
  disabled?: boolean;
};

const RunButton: FC<RunButtonProps> = ({ hosts, onClick, disabled = false }) => {
  const [selectedHost, setSelectedHost] = useState<string>('undefined');
  const options: Array<PopoverActionMenuOption<IconType, string, string>> = [
    {
      text: 'Execute',
      icon: 'runCircle',
      action: 'undefined'
    },
    ...hosts.map(host => ({ text: `Execute (on ${host})`, icon: 'runCircle' as const, action: `${host}` }))
  ];
  const selectedOption = options.find(option => option.action === selectedHost)!;

  return (
    <ActionPopover
      disabled={disabled}
      options={options}
      defaultAction={selectedOption.action}
      defaultActionTitle={selectedOption.text}
      onSelect={(action: string) => {
        setSelectedHost(action);
      }}
      onClick={selected => onClick(selected === 'undefined' ? undefined : selected)}
    />
  );
};

export const PackagePopoutProducer: FC<PackagePopoutProducerProps> = ({
  producer,
  producerKey,
  flowKey,
  pipelineKey,
  accountUuid,
  packageVersionUuid,
  services
}) => {
  const [executeCommand, { data: executeCommandResult, loading: executingCommand }] = useExecuteButtonCommandMutation();
  let serviceType: string | undefined;
  mapRecord(producer.instances, (instance, instanceKey) => {
    serviceType = filterServiceType(services, instanceKey);
  });

  const comp = ComponentFactory.get(producer.type);
  const svgWrapperRef = useRef<HTMLDivElement | null>(null);

  if (svgWrapperRef.current) {
    svgWrapperRef.current.innerHTML = comp?.icon || '';
  }

  return (
    <FlexLayout
      flexDirection="column"
      border="regular"
      css={{ padding: '5px', margin: '5px', borderRadius: '15px', minHeight: '50px', minWidth: '50px' }}
    >
      <FlexLayout flexDirection="row" width="full" justifyContent="center">
        <Box ref={svgWrapperRef} css={{ paddingRight: '10px' }} />
        <H2>{producerKey}</H2>
      </FlexLayout>
      <TableView gridTemplateColumns="0.5fr 1fr">
        <TRow key="rowType" rowHeight="40px">
          <>
            <Box>Type</Box>
            <Box alignContent="right" width="full">
              {producer.type}
            </Box>
          </>
        </TRow>
        <TRow key="rowInstances" rowHeight="40px">
          <>
            <Box>Instances</Box>
            <Box>
              {producer.initialized}/{producer.instanceCount}
              {serviceType && ` ${serviceType}`}
            </Box>
          </>
        </TRow>
        <TRow key="rowMessages" rowHeight="40px">
          <>
            <Box>Messages</Box>
            <Box alignContent="right" width="full">
              {producer.count}
            </Box>
          </>
        </TRow>
        <TRow key="rowSince" rowHeight="40px">
          <>
            <Box>Since</Box>
            <Box>{toLocalDateString(producer.since || '')}</Box>
          </>
        </TRow>
        {producer.type === 'button' && (
          <TRow key="rowButton" rowHeight="40px">
            <>
              <Box key="label">Execute</Box>
              <Box key="button">
                <RunButton
                  disabled={executingCommand}
                  key="runButton"
                  hosts={mapRecordToArray(producer.instances, (a, instanceKey) => instanceKey)}
                  onClick={instanceKey => {
                    executeCommand({
                      variables: {
                        uuid: v4(),
                        accountUuid,
                        packageVersionUuid,
                        flowUuid: flowKey,
                        pipelineKey,
                        componentKey: producerKey,
                        host: instanceKey
                      }
                    });

                    return true;
                  }}
                />
              </Box>
            </>
          </TRow>
        )}
      </TableView>
    </FlexLayout>
  );
};
